import { defineComponent as _defineComponent } from 'vue'
import { ActivityImportMappedProperty, ActivityImportStoreMappingsRequestAllOfMappingsInner } from 'farmdok-rest-api';
import Handsontable from 'handsontable';
import Vue, { computed, onBeforeMount, onUnmounted, ref } from 'vue';

import TableActivitiesLoading from '@/activities/components/table/TableActivitiesLoading.vue';
import { Getters as ImportActivitiesGetters } from '@/activities/importActivities/store/getters';
import {
  ImportActivityEquipmentCombined,
  ImportActivityProductCombined,
} from '@/activities/importActivities/store/types';
import type { TableDataImportActivity } from '@/activities/importActivities/types/TableDataImportActivity.types';
import ActivityEquipmentService from '@/activities/services/ActivityEquipmentService';
import type { CellChangeWithIdsActivityTable } from '@/activities/types';
import calculateAmountsAndUnits from '@/activities/utils/amountsAndUnits/calculateAmountsAndUnits';
import CompanyService from '@/auth/services/CompanyService';
import EquipmentService from '@/equipments/services/EquipmentService';
import { Api } from '@/plugins/farmdokRestApi';
import ProductService from '@/products/services/ProductService';
import type { Unit } from '@/shared/api/rest/models';
import TableFooterContainer from '@/shared/containers/TableFooterContainer.vue';
import TableColumnsService from '@/shared/handsontable/rework/services/TableColumnsService';
import type { ColumnSettingsWithUserSettings } from '@/shared/handsontable/rework/types';
import type { Data } from '@/shared/mixins/store/types';
import store from '@/store';

import TableActivities from '../../components/table/TableActivities.vue';
import ActivityProductService from '../../services/ActivityProductService';
import columns from '../handsontable/columns';


export default /*#__PURE__*/_defineComponent({
  __name: 'TableActivitiesImportContainer',
  setup(__props, { expose }) {

const { activityImportApi } = Vue.prototype.$api as Api;

const tableElement = ref();

/* -- COMPUTED -- */

const tableData = computed<TableDataImportActivity[]>(
  () => store.getters['activities/importActivities/tableDataImportActivities'],
);

const loading = computed<boolean>(
  () =>
    store.getters['activities/importActivities/loading'] ||
    store.getters['activityTypes/loading'] ||
    store.getters['customers/loading'] ||
    store.getters['equipments/loading'] ||
    store.getters['fields/loading'] ||
    store.getters['productCategories/loading'] ||
    store.getters['pests/loading'] ||
    store.getters['storagePlaces/loading'] ||
    store.getters['units/loading'] ||
    store.getters['users/loading'] ||
    store.getters['products/loading'],
);

const tableColumns = computed<ColumnSettingsWithUserSettings[]>(() => {
  const { userColumnSettings } = store.state.activities.importActivities;
  const tableColumnsService = new TableColumnsService(columns);
  return tableColumnsService.getColumnsForHandsontable(userColumnSettings);
});

const fixedColumnsLeft = computed<number>(() => {
  const fixedColumnsIndex = tableColumns.value.findIndex((column) => column.fixedColumnsLeftUntilHere === true);
  return fixedColumnsIndex === -1 ? 0 : fixedColumnsIndex + 1;
});

const units = computed<Data<Unit>>(() => store.state.units.data);

// Services
const productService = computed<ProductService>(
  () =>
    new ProductService(
      store.state.products.mineralFertilizers.data,
      store.state.products.companyFertilizers.data,
      store.state.products.secondaryFertilizers.data,
      store.state.products.herbizides.data,
      store.state.products.crops.data,
      store.state.products.miscellaneous.data,
      store.state.products.harvests.data,
      store.state.products.seeds.data,
      store.state.products.otherProductsAndFertilizers.data,
    ),
);
const activityProductService = computed<ActivityProductService>(
  () => new ActivityProductService(units.value, productService.value, calculateAmountsAndUnits),
);
const equipmentService = computed<EquipmentService>(() => new EquipmentService(store.state.equipments.data));
const activityEquipmentService = computed<ActivityEquipmentService>(
  () => new ActivityEquipmentService(equipmentService.value),
);
const companyService = computed<CompanyService>(() => new CompanyService(store.state.auth.companiesById));

onBeforeMount(() => {
  loadData();
});

onUnmounted(() => {
  resetStoreTableData();
});

/* -- METHODS -- */
function loadData() {
  store.dispatch('activities/importActivities/subscribe', { forceRefresh: true });
  store.dispatch('equipments/subscribe');
  store.dispatch('fields/subscribe');
  store.dispatch('activityTypes/subscribe');
  store.dispatch('customers/subscribe');
  store.dispatch('products/subscribe');
  store.dispatch('productCategories/subscribe');
  store.dispatch('pests/subscribe');
  store.dispatch('storagePlaces/subscribe');
  store.dispatch('units/subscribe');
  store.dispatch('users/subscribe');
  store.dispatch('activities/subscribe');
}

function resetStoreTableData() {
  store.commit('activities/importActivities/resetTableData');
  store.commit('activities/importActivities/resetTableDataMetadata');
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function onChange(changes: CellChangeWithIdsActivityTable[], source: Handsontable.ChangeSource) {
  changes.forEach((change) => {
    const [, key, , newValue, { id: importActivityId }] = change;
    const metadataKeys = ['expand', 'select'];

    if (key.startsWith('product')) {
      onChangeActivityProduct(change);
      return;
    }

    if (key.startsWith('equipment')) {
      onChangeActivityEquipment(change);
      return;
    }

    if (key === 'fieldDropdownItem') {
      onChangeFieldDropdownItem(change);
      return;
    }

    if (metadataKeys.includes(key)) {
      store.commit('activities/importActivities/addOrUpdateTableDataMetadataByKeyAndValue', {
        importActivityId,
        key,
        value: newValue,
      });
      return;
    }

    if (key === 'activityTypeDropdownItem') {
      onChangeActivityTypeDropdownItem(change);
      return;
    }

    updateImportActivityEntryByKeyAndValue(importActivityId, key, newValue);
  });
}

function onChangeActivityTypeDropdownItem(change: CellChangeWithIdsActivityTable) {
  const { id: activityTypeId } = change[3];
  const { id: importActivityId } = change[4];
  const importActivity = (store.getters['activities/importActivities/getById'] as ImportActivitiesGetters['getById'])(
    importActivityId,
  );
  const companyId = importActivity.processOrderId
    ? companyService.value.getCompanyByProcessOrder(importActivity.processOrderId).id
    : '';

  const { originalValue } = importActivity.activityTypeIdMatch;
  if (originalValue && !importActivity.activityTypeId) {
    Object.values(store.state.activities.importActivities.data).forEach((activity) => {
      if (activity.activityTypeIdMatch.originalValue === originalValue) {
        store.dispatch('activities/importActivities/updateActivityType', {
          importActivityId: activity.id,
          importActivityTypeId: activityTypeId,
        });
      }
    });
    const activityMappings: ActivityImportStoreMappingsRequestAllOfMappingsInner[] = [
      {
        externalKey: importActivity.activityTypeIdMatch.originalKey || '',
        mappedProperty: ActivityImportMappedProperty.ActivityTypeId,
        farmdokId: activityTypeId,
      },
    ];
    postActivityImportStoreMappings(companyId, activityMappings);
  }

  store.dispatch('activities/importActivities/updateActivityType', {
    importActivityId,
    importActivityTypeId: activityTypeId,
  });
}

function onChangeActivityProduct(change: CellChangeWithIdsActivityTable) {
  const { id: importActivityId } = change[4];
  const importActivity = (store.getters['activities/importActivities/getById'] as ImportActivitiesGetters['getById'])(
    importActivityId,
  );
  const previousValue = change[2]?.name;
  const companyId = importActivity.processOrderId
    ? companyService.value.getCompanyByProcessOrder(importActivity.processOrderId).id
    : '';

  // if previous value is the same as the original value, we update other similar import activities with the same product
  if (previousValue && previousValue === importActivity.products[0]?.productIdMatch?.originalValue) {
    Object.values(store.state.activities.importActivities.data).forEach((activity) => {
      activity.products.forEach((importActivityProduct: ImportActivityProductCombined) => {
        if (
          previousValue === importActivityProduct.productIdMatch?.originalValue &&
          activity.activityTypeId === importActivity.activityTypeId &&
          activity.fieldId === importActivity.fieldId
        ) {
          const changeForActivityProduct = [...change];
          changeForActivityProduct[4].activityProductId = importActivityProduct.id;
          const updatedActivityProducts = activityProductService.value.updateActivityProductsFromChange(
            activity.products,
            changeForActivityProduct as CellChangeWithIdsActivityTable,
            companyId,
          );

          updateImportActivityEntryByKeyAndValue(activity.id, 'products', updatedActivityProducts);
        }
      });
    });
    const productMappings: ActivityImportStoreMappingsRequestAllOfMappingsInner[] = [
      {
        externalKey: importActivity.products[0]?.productIdMatch?.originalKey || '',
        mappedProperty: ActivityImportMappedProperty.ProductId,
        farmdokId: change[3].id,
      },
    ];
    postActivityImportStoreMappings(companyId, productMappings);
  } else {
    const updatedActivityProducts = activityProductService.value.updateActivityProductsFromChange(
      importActivity.products,
      change,
      companyId,
    );
    updateImportActivityEntryByKeyAndValue(importActivityId, 'products', updatedActivityProducts);
  }
}

function onChangeActivityEquipment(change: CellChangeWithIdsActivityTable) {
  const { id: importActivityId } = change[4];
  const importActivity = (store.getters['activities/importActivities/getById'] as ImportActivitiesGetters['getById'])(
    importActivityId,
  );
  const companyId = importActivity.processOrderId
    ? companyService.value.getCompanyByProcessOrder(importActivity.processOrderId).id
    : '';
  const previousValue = change[2]?.name;

  // if previous value is the same as the original value, we update other similar import activities with the same equipment
  if (previousValue && previousValue === importActivity.equipment[0]?.equipmentIdMatch?.originalValue) {
    Object.values(store.state.activities.importActivities.data).forEach((activity) => {
      activity.equipment.forEach((importActivityEquipment: ImportActivityEquipmentCombined) => {
        if (previousValue === importActivityEquipment.equipmentIdMatch.originalValue) {
          const changeForActivityEquipment = [...change];
          changeForActivityEquipment[4].activityEquipmentId = importActivityEquipment.id;
          const updatedActivityEquipment = activityEquipmentService.value.updateActivityEquipmentsFromChange(
            activity.equipment,
            changeForActivityEquipment as CellChangeWithIdsActivityTable,
            companyId,
          );

          updateImportActivityEntryByKeyAndValue(activity.id, 'equipment', updatedActivityEquipment);
        }
      });
    });
    const equipmentMappings: ActivityImportStoreMappingsRequestAllOfMappingsInner[] = [
      {
        externalKey: importActivity.equipment[0]?.equipmentIdMatch?.originalKey || '',
        mappedProperty: ActivityImportMappedProperty.EquipmentId,
        farmdokId: change[3].id,
      },
    ];
    postActivityImportStoreMappings(companyId, equipmentMappings);
  } else {
    const updatedActivityEquipment = activityEquipmentService.value.updateActivityEquipmentsFromChange(
      importActivity.equipment,
      change,
      companyId,
    );
    updateImportActivityEntryByKeyAndValue(importActivityId, 'equipment', updatedActivityEquipment);
  }
}

function onChangeFieldDropdownItem(change: CellChangeWithIdsActivityTable) {
  const { id: fieldId } = change[3];
  const { id: importActivityId } = change[4];
  const updatedField = store.state.fields.data[fieldId];
  const importActivity = (store.getters['activities/importActivities/getById'] as ImportActivitiesGetters['getById'])(
    importActivityId,
  );
  const companyId = importActivity.processOrderId
    ? companyService.value.getCompanyByProcessOrder(importActivity.processOrderId).id
    : '';

  const { originalValue } = importActivity.fieldIdMatch;
  if (originalValue && !importActivity.fieldId) {
    Object.values(store.state.activities.importActivities.data).forEach((activity) => {
      if (activity.fieldIdMatch.originalValue === originalValue) {
        updateImportActivityEntryByKeyAndValue(activity.id, 'fieldId', fieldId);
        updateImportActivityEntryByKeyAndValue(activity.id, 'processedArea', updatedField.fieldSize);
      }
    });
    const fieldMappings: ActivityImportStoreMappingsRequestAllOfMappingsInner[] = [
      {
        externalKey: importActivity.fieldIdMatch.originalKey || '',
        mappedProperty: ActivityImportMappedProperty.FieldId,
        farmdokId: fieldId,
      },
    ];
    postActivityImportStoreMappings(companyId, fieldMappings);
  }

  updateImportActivityEntryByKeyAndValue(importActivityId, 'processedArea', updatedField.fieldSize);
  updateImportActivityEntryByKeyAndValue(importActivityId, 'fieldId', fieldId);
}

function updateImportActivityEntryByKeyAndValue(guid: string, key: string, value: any) {
  store.commit('activities/importActivities/updateEntryByKeyAndValue', {
    guid,
    key,
    value,
  });
}

function postActivityImportStoreMappings(
  companyId: string,
  mappings: ActivityImportStoreMappingsRequestAllOfMappingsInner[],
) {
  activityImportApi.activityImportStoreMappings({
    activityImportStoreMappingsRequest: {
      companyId,
      mappings,
    },
  });
}

expose({
  tableElement,
});

return { __sfc: true,activityImportApi, tableElement, tableData, loading, tableColumns, fixedColumnsLeft, units, productService, activityProductService, equipmentService, activityEquipmentService, companyService, loadData, resetStoreTableData, onChange, onChangeActivityTypeDropdownItem, onChangeActivityProduct, onChangeActivityEquipment, onChangeFieldDropdownItem, updateImportActivityEntryByKeyAndValue, postActivityImportStoreMappings, TableActivitiesLoading, TableFooterContainer, TableActivities }
}

})